import { DOMParser } from '@xmldom/xmldom';
import type { Element } from '@xmldom/xmldom';
import { REGIONAL_FEDERATIONS, NATIONS, lvName } from '../domain/constants';
import { impliedSex, reverseDiscipline } from '../domain/disciplines';
import { newAthlete, newStarter, uid } from '../domain/workspace';
import { t } from '../i18n';
import type { Athlete, Competition, Contact, Sex, Starter, StarterType } from '../types';

/**
 * One `<Starter>` (kunstradmeldung) or `<Team>` (teammeldung) block. Only
 * `figureNrs` is taken from the programme's `<Uebung>` elements — never the
 * XML's own `Deutsch`/`Englisch`/`Punkte`, which are rebuilt from Kür's own
 * catalogue instead (see `src/domain/importWebo.ts`, `buildWeboProgramme()`).
 */
export interface WeboEntry {
  id: string;
  starter: Starter;
  athletes: Athlete[];
  clubNames: readonly string[];
  /** `Uebung/Nr`, in `Reihenfolge` order. Always empty for teammeldung —
   *  that schema carries no figure data at all. */
  figureNrs: readonly string[];
  /** `Kopf/Aufgestellt`, a sanity-check input only. 0 for teammeldung. */
  declaredTotal: number;
  label: string;
}

export interface WeboImport {
  schema: 'kunstradmeldung' | 'teammeldung';
  contact: Contact | null;
  /** `null` only if the file has no `Verantwortlich`/root fields to read at
   *  all — in practice always present, since both schemas require them. */
  meta: { club: string; lv: string; federation: string } | null;
  /** Always `null` for teammeldung — that schema has no `Wettbewerb`. */
  competition: Competition | null;
  entries: WeboEntry[];
  /** Non-blocking sanity-check findings (unknown nation/Landesverband,
   *  a Disziplin that didn't match a known combination), already translated. */
  warnings: string[];
}

function firstChild(parent: Element, tag: string): Element | null {
  const list = parent.getElementsByTagName(tag);
  return list.length > 0 ? list[0]! : null;
}

function text(parent: Element, tag: string): string {
  return firstChild(parent, tag)?.textContent?.trim() ?? '';
}

function inferTypeFromRiderCount(count: number): StarterType {
  if (count === 2) return 'pair';
  if (count === 4) return 'team4';
  if (count === 6) return 'team6';
  return 'single';
}

function extractContact(el: Element | null): Contact | null {
  if (!el) return null;
  return {
    firstName: text(el, 'VornameVerantwortlich'),
    lastName: text(el, 'NachnameVerantwortlich'),
    uciId: text(el, 'UCIIdVerantwortlich'),
    countryCode: text(el, 'LKZVerantwortlich'),
    postalCode: text(el, 'PLZVerantwortlich'),
    city: text(el, 'OrtVerantwortlich'),
    street: text(el, 'StrasseVerantwortlich'),
    phone: text(el, 'TelefonVerantwortlich'),
    fax: text(el, 'FaxVerantwortlich'),
    email: text(el, 'EmailVerantwortlich'),
  };
}

function extractCompetition(el: Element): Competition {
  return {
    id: uid(),
    name: text(el, 'Wettbewerbsname'),
    date: text(el, 'Wettbewerbsdatum'),
    venue: text(el, 'Wettbewerbsort'),
    organiser: text(el, 'Veranstalter'),
  };
}

interface ExtractedAthlete {
  athlete: Athlete;
  clubName: string;
}

function checkNation(athlete: Athlete, warnings: string[]): void {
  if (athlete.nation !== '' && !NATIONS.includes(athlete.nation)) {
    warnings.push(t('webo.warning.unknownNation', { name: `${athlete.firstName} ${athlete.lastName}`.trim(), nation: athlete.nation }));
  }
}

function extractKunstradAthlete(el: Element, warnings: string[]): ExtractedAthlete {
  const athlete: Athlete = {
    ...newAthlete(),
    firstName: text(el, 'Vorname'),
    lastName: text(el, 'Nachname'),
    uciId: text(el, 'UCIId'),
    birthDate: text(el, 'Gebdat'),
    sex: text(el, 'Geschlecht') === 'm' ? 'm' : 'w',
    nation: text(el, 'Nation') || 'GER',
    countryCode: text(el, 'LKZ'),
    postalCode: text(el, 'PLZ'),
    city: text(el, 'Ort'),
    street: text(el, 'Strasse'),
    phone: text(el, 'Telefon'),
    fax: text(el, 'Fax'),
    email: text(el, 'Email'),
  };
  checkNation(athlete, warnings);
  return { athlete, clubName: text(el, 'Verein') };
}

/** teammeldung's `Sportler` has no `Geschlecht` field at all — `defaultSex`
 *  (the discipline class's implied sex, or 'm' for an open class) fills it
 *  in, since `Athlete.sex` is required. */
function extractTeamAthlete(el: Element, defaultSex: Sex, warnings: string[]): ExtractedAthlete {
  const athlete: Athlete = {
    ...newAthlete(),
    firstName: text(el, 'Vorname'),
    lastName: text(el, 'Nachname'),
    uciId: text(el, 'UCIId'),
    birthDate: text(el, 'Gebdat'),
    sex: defaultSex,
    nation: text(el, 'Nation') || 'GER',
  };
  checkNation(athlete, warnings);
  return { athlete, clubName: text(el, 'Verein') };
}

function entryLabel(starter: Starter, athletes: readonly Athlete[], discipline: string): string {
  const name = starter.name.trim() || athletes.map((a) => a.lastName.trim() || a.firstName.trim()).join(' / ') || '?';
  return t('webo.item.entry', { name, discipline });
}

function checkDisziplin(discipline: string, riderCount: number, warnings: string[]): StarterType {
  const reversed = reverseDiscipline(discipline);
  if (reversed) return reversed.type;
  warnings.push(t('webo.warning.unknownDiscipline', { discipline, count: riderCount }));
  return inferTypeFromRiderCount(riderCount);
}

function extractKunstradEntry(starterEl: Element, warnings: string[]): WeboEntry {
  const kopf = firstChild(starterEl, 'Kopf');
  const discipline = kopf ? text(kopf, 'Disziplin') : '';
  const extracted = Array.from(starterEl.getElementsByTagName('Sportler')).map((el) => extractKunstradAthlete(el, warnings));
  const type = checkDisziplin(discipline, extracted.length, warnings);

  const starter: Starter = {
    ...newStarter(),
    type,
    discipline,
    disciplineEn: kopf ? text(kopf, 'DisziplinEnglisch') : '',
    name: kopf ? text(kopf, 'Startername') : '',
    personalBest: {
      points: kopf ? text(kopf, 'PunkteBestleistung') : '',
      date: kopf ? text(kopf, 'DatumBestleistung') : '',
      venue: kopf ? text(kopf, 'OrtBestleistung') : '',
    },
    scoringReportEmail: kopf ? text(kopf, 'Emailwertungsbericht') : '',
    athleteIds: extracted.map(({ athlete }) => athlete.id),
  };

  const figureNrs = Array.from(starterEl.getElementsByTagName('Uebung'))
    .map((u) => ({ reihenfolge: Number(text(u, 'Reihenfolge')) || 0, nr: text(u, 'Nr') }))
    .sort((a, b) => a.reihenfolge - b.reihenfolge)
    .map((u) => u.nr)
    .filter((nr) => nr !== '');

  const athletes = extracted.map(({ athlete }) => athlete);
  return {
    id: uid(),
    starter,
    athletes,
    clubNames: [...new Set(extracted.map(({ clubName }) => clubName).filter((n) => n !== ''))],
    figureNrs,
    declaredTotal: Number(kopf ? text(kopf, 'Aufgestellt') : '') || 0,
    label: entryLabel(starter, athletes, discipline),
  };
}

function extractTeamEntry(teamEl: Element, warnings: string[]): WeboEntry {
  const discipline = text(teamEl, 'Disziplin');
  const defaultSex = impliedSex(discipline) ?? 'm';
  const extracted = Array.from(teamEl.getElementsByTagName('Sportler')).map((el) => extractTeamAthlete(el, defaultSex, warnings));
  const type = checkDisziplin(discipline, extracted.length, warnings);

  const starter: Starter = {
    ...newStarter(),
    type,
    discipline,
    disciplineEn: '',
    name: text(teamEl, 'TeamName'),
    athleteIds: extracted.map(({ athlete }) => athlete.id),
  };

  const athletes = extracted.map(({ athlete }) => athlete);
  return {
    id: uid(),
    starter,
    athletes,
    clubNames: [...new Set(extracted.map(({ clubName }) => clubName).filter((n) => n !== ''))],
    // teammeldung.xsd has no Uebungen/Uebung element at all — nothing to read.
    figureNrs: [],
    declaredTotal: 0,
    label: entryLabel(starter, athletes, discipline),
  };
}

function parseKunstradmeldung(root: Element): WeboImport {
  const warnings: string[] = [];
  const contact = extractContact(firstChild(root, 'Verantwortlich'));

  const lvCode = text(root, 'LV');
  const lv = lvCode === '' ? '' : lvName(lvCode);
  if (lvCode !== '' && lv === undefined) {
    warnings.push(t('webo.warning.unknownRegionalFederation', { value: lvCode }));
  }

  const starterEls = Array.from(root.getElementsByTagName('Starter'));
  const federation = starterEls
    .map((el) => {
      const kopf = firstChild(el, 'Kopf');
      return kopf ? text(kopf, 'Verband') : '';
    })
    .find((v) => v !== '') ?? '';

  const wettbewerbEl = firstChild(root, 'Wettbewerb');
  const competition = wettbewerbEl ? extractCompetition(wettbewerbEl) : null;

  return {
    schema: 'kunstradmeldung',
    contact,
    meta: { club: text(root, 'VereinMeldung'), lv: lv ?? '', federation },
    competition,
    entries: starterEls.map((el) => extractKunstradEntry(el, warnings)),
    warnings,
  };
}

function parseTeammeldung(root: Element): WeboImport {
  const warnings: string[] = [];
  const contact = extractContact(firstChild(root, 'Verantwortlich'));

  const lv = text(root, 'LandesverbandMeldung');
  if (lv !== '' && !REGIONAL_FEDERATIONS.some(([name]) => name === lv)) {
    warnings.push(t('webo.warning.unknownRegionalFederation', { value: lv }));
  }

  const teamEls = Array.from(root.getElementsByTagName('Team'));
  const federation = teamEls.map((el) => text(el, 'Bezirk')).find((v) => v !== '') ?? '';

  return {
    schema: 'teammeldung',
    contact,
    meta: { club: text(root, 'VereinMeldung'), lv, federation },
    // teammeldung.xsd carries no competition data at all (only a bare
    // Kalenderjahr year, which is too little information to build a
    // Competition record from) — see CLAUDE.md-adjacent decision in the plan.
    competition: null,
    entries: teamEls.map((el) => extractTeamEntry(el, warnings)),
    warnings,
  };
}

/**
 * Parse a real WeBo2008-Qt export — either schema. Lenient by design, same
 * "accept without trusting" posture as `parseWorkspace()`
 * (`src/domain/workspace.ts`): a missing/malformed field just reads as empty
 * rather than failing the whole import. Only throws when there is
 * genuinely nothing sensible to work with: unparseable XML, an
 * unrecognized root element, or zero `Starter`/`Team` blocks.
 */
export function parseWeboXml(xmlText: string): WeboImport {
  let doc;
  try {
    doc = new DOMParser({ onError: () => {} }).parseFromString(xmlText, 'text/xml');
  } catch {
    throw new Error('Malformed XML.');
  }
  const root = doc.documentElement;
  if (!root) throw new Error('No root element.');

  let imp: WeboImport;
  if (root.tagName === 'Kunstradmeldung') {
    imp = parseKunstradmeldung(root);
  } else if (root.tagName === 'Teammeldung') {
    imp = parseTeammeldung(root);
  } else {
    throw new Error('Unrecognized root element.');
  }
  if (imp.entries.length === 0) throw new Error('No Starter/Team blocks found.');
  return imp;
}
